import { useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { RunStatus } from '../../../../backend/src/types/types';
import { useGetSubsets } from '@/api/queries/useGetSubsets';
import formatDate from '@/helpers/formatDate';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button, type ButtonProps } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { isProcessingStatus } from '@/new/lib/pipeline-status';
import { cn } from '@/new/lib/utils';

interface SubsetDropdownProps {
  matterId: string;
  subsetId: string;
  docIds?: string[];
  getDocIds?: () => Promise<string[] | null>;
  disabled?: boolean;
  size?: 'sm' | 'default' | 'lg';
  variant?: ButtonProps['variant'];
  triggerText: string;
  onCreateNew: () => void;
  isLoading?: boolean;
}

export function SubsetDropdown({
  matterId,
  subsetId,
  docIds,
  getDocIds,
  disabled,
  size,
  variant,
  triggerText,
  onCreateNew,
  isLoading,
}: SubsetDropdownProps) {
  const navigate = useNavigate();
  const [isAddToSubsetDialogOpen, setIsAddToSubsetDialogOpen] = useState(false);
  const [resolvedDocIds, setResolvedDocIds] = useState<string[] | null>(null);

  const { data: allSubsets = [], isLoading: isSubsetsLoading } = useGetSubsets(matterId);

  const subsets = useMemo(
    () => allSubsets.filter((s) => !s.isAllDocuments && s.id !== subsetId && s.status !== 'processing'),
    [allSubsets, subsetId],
  );

  const handleAddToExisting = async () => {
    if (getDocIds) {
      const ids = await getDocIds();
      if (!ids) return;
      setResolvedDocIds(ids);
    }
    setIsAddToSubsetDialogOpen(true);
  };

  const handleAddToSubset = (targetSubsetId: string) => {
    const ids = resolvedDocIds ?? docIds ?? [];
    setIsAddToSubsetDialogOpen(false);
    setResolvedDocIds(null);
    navigate(`/matters/${matterId}/subsets/${targetSubsetId}/edit`, {
      state: { additionalDocIds: ids },
    });
  };

  const docCount = (resolvedDocIds ?? docIds ?? []).length;
  const isDisabled = disabled || isLoading;

  return (
    <>
      <PermissionGate permission={Permission.SubsetCreate} uiMode="disabled">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size={size} variant={variant} disabled={isDisabled}>
              <Icon className={isLoading ? 'lucide--loader animate-spin ease-in-out' : 'lucide--group'} />
              {triggerText}
              <Icon className="lucide--chevron-down" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" className={cn(size === 'sm' && 'text-xs')}>
            <DropdownMenuItem className={cn(size === 'sm' && 'text-xs')} onClick={onCreateNew}>
              <Icon className="lucide--plus" /> Create New
            </DropdownMenuItem>
            <DropdownMenuItem className={cn('pl-1.75', size === 'sm' && 'text-xs')} onClick={handleAddToExisting}>
              <Icon className="lucide--list-plus" />
              Add to Existing
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PermissionGate>

      <Dialog open={isAddToSubsetDialogOpen} onOpenChange={setIsAddToSubsetDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              <Icon className="lucide--group" />
              Add to Existing Subset
            </DialogTitle>
            <DialogDescription>
              Add {docCount} {docCount === 1 ? 'document' : 'documents'} to an existing subset. Documents already in the
              subset will be skipped.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center justify-start">
            <div className="w-full">
              {isSubsetsLoading ? (
                <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500">
                  <Icon className="lucide--loader animate-spin ease-in-out" />
                  Loading Subsets...
                </div>
              ) : subsets.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500">
                  No existing subsets found. Use &quot;Create New&quot; to create one.
                </p>
              ) : (
                <div className="flex w-full flex-col justify-between gap-2">
                  {subsets.map((subset) => {
                    const hasActiveRun = subset.runs?.some(
                      (run) => run.status && isProcessingStatus(run.status as RunStatus),
                    );
                    return (
                      <div
                        key={subset.id}
                        className="flex items-center justify-between rounded-md border bg-gradient-to-r from-gray-50 px-4 py-2.5 pr-2.5 text-sm"
                      >
                        <div className="flex flex-col">
                          <p className="line-clamp-1 font-medium text-ellipsis">{subset.name}</p>
                          <p className="text-xs text-gray-500">
                            Created {formatDate(subset.createdDate)}
                            {subset.docCount != null && ` · ${subset.docCount} documents`}
                          </p>
                        </div>
                        {hasActiveRun ? (
                          <p className="flex items-center gap-1.5 pr-2 text-xs text-sky-500">
                            <Icon className="lucide--info mb-px" />
                            Waiting for processing to finish
                          </p>
                        ) : (
                          <Button className="bg-gray-100" onClick={() => handleAddToSubset(subset.id)}>
                            <Icon className="lucide--plus" /> Add
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
